/**
 * Sanitizes error messages for display to users.
 * Converts technical errors into friendly messages and applies security filtering.
 */
export function sanitizeError(error: unknown): string {
  // Extract message from various error types
  let message: string;

  if (error instanceof Error) {
    ({ message } = error);
  } else if (typeof error === 'string') {
    message = error;
  } else if (error === null || error === undefined) {
    return 'Something went wrong. Try again, and get in touch if it keeps happening.';
  } else {
    // For objects, try to extract message property or convert to string
    const errorObj = error as { message?: unknown };
    const rawMessage = errorObj.message;
    message = typeof rawMessage === 'string' ? rawMessage : String(error);
  }

  const lowerMessage = message.toLowerCase();

  // Security filtering: Detect and block technical leakage
  if (
    message.startsWith('/') || // Unix file paths
    /^[a-z]:[\\/]/i.test(message) || // Windows absolute paths (C:\, C:/, D:\, D:/, etc.)
    message.startsWith('..\\') || // Windows relative parent paths (..\)
    message.startsWith('.\\') || // Windows relative paths (.\)
    message.startsWith('../') || // Unix relative parent paths
    message.startsWith('./') || // Unix relative paths
    /\bat\s[A-Za-z]/.test(message) || // Stack traces (e.g., "Error at processRequest", " at Object.")
    message.startsWith('{"') || // JSON blobs
    message.includes('eyJ') || // JWT tokens
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(
      message
    ) || // UUIDs
    /\S+@\S+\.\S+/.test(message) // Email addresses
  ) {
    return 'Something went wrong. Try again, and get in touch if it keeps happening.';
  }

  // Detect toString artifacts
  if (message === '[object Error]' || message === '[object Object]') {
    return 'Something went wrong. Try again, and get in touch if it keeps happening.';
  }

  // Auth-specific error mappings
  if (
    lowerMessage.includes('credentials') ||
    lowerMessage.includes('authentication') ||
    (lowerMessage.includes('invalid') &&
      (lowerMessage.includes('password') ||
        lowerMessage.includes('credential') ||
        lowerMessage.includes('token') ||
        lowerMessage.includes('login')))
  ) {
    return 'The email or password you entered is incorrect. Please check your credentials and try again.';
  }

  // Duplicate email check (email already taken/registered/in use)
  if (
    lowerMessage.includes('email') &&
    (lowerMessage.includes('already') ||
      lowerMessage.includes('taken') ||
      lowerMessage.includes('registered') ||
      lowerMessage.includes('in use'))
  ) {
    return 'An account with that email address already exists. Please log in or use a different email.';
  }

  // Email not found check
  if (lowerMessage.includes('email') && lowerMessage.includes('exist')) {
    return "We couldn't find an account with that email address.";
  }

  if (
    lowerMessage.includes('password') &&
    (lowerMessage.includes('wrong') || lowerMessage.includes('incorrect'))
  ) {
    return 'The password you entered is incorrect.';
  }

  if (
    lowerMessage.includes('account') &&
    (lowerMessage.includes('locked') || lowerMessage.includes('suspended'))
  ) {
    return 'Your account has been temporarily locked.';
  }

  if (
    lowerMessage.includes('rate limit') ||
    lowerMessage.includes('rate-limit') ||
    lowerMessage.includes('ratelimit') ||
    lowerMessage.includes('too many')
  ) {
    return 'Too many attempts. Please wait a moment.';
  }

  // Network error mappings
  if (
    lowerMessage.includes('fetch') ||
    lowerMessage.includes('network') ||
    lowerMessage.includes('err_') ||
    lowerMessage.includes('econnrefused') ||
    lowerMessage.includes('failed to fetch')
  ) {
    return 'Unable to connect. Please check your connection and try again.';
  }

  // Timeout errors
  if (lowerMessage.includes('timeout') || lowerMessage.includes('etimedout')) {
    return 'The request timed out. Please try again.';
  }

  // HTTP status code stripping
  // Matches patterns like:
  // - "Export failed: 500 Internal Server Error"
  // - "500 Internal Server Error"
  // - "Failed with status 500"
  // - "Error 404: Not Found"

  // Pattern 1: "prefix: 500 ..." → "prefix. Please try again."
  const prefixedStatusMatch = message.match(/^(.+?):\s*\d{3}\s+/);
  if (prefixedStatusMatch) {
    return `${prefixedStatusMatch[1]}. Please try again.`;
  }

  // Pattern 2: Starts with status code "500 Internal Server Error" or bare "500" → generic error
  if (/^\d{3}(\s+|$)/.test(message)) {
    return 'Something went wrong. Try again, and get in touch if it keeps happening.';
  }

  // Pattern 3: Contains "status 500" or "error 500" anywhere → strip to generic
  if (/\b(status|error|code)\s+\d{3}\b/i.test(message)) {
    return 'Something went wrong. Try again, and get in touch if it keeps happening.';
  }

  // Check if message is already user-friendly (no technical indicators)
  const technicalIndicators = [
    'error:',
    'exception',
    'undefined',
    'null',
    'cannot read',
    'is not a ',
    'is not defined',
    'is not a function',
    'failed:',
    'errno',
    'code:',
  ];

  const hasTechnicalIndicators = technicalIndicators.some(indicator =>
    lowerMessage.includes(indicator)
  );

  // A message can lack the technicalIndicators substrings above and still leak
  // backend internals (snake_case/kebab-case identifiers, structured data,
  // long dumps). Plain validation copy legitimately contains numbers (e.g.
  // "Deals over 10k need approval"), so digits alone are not a signal — only
  // identifier-shaped tokens, structural punctuation, and excessive length
  // are treated as raw technical detail. Anything matching falls back to the
  // generic message rather than being shown verbatim.
  const looksLikeRawTechnicalDetail =
    message.length > 120 ||
    /[a-z0-9]+_[a-z0-9_]+/i.test(message) ||
    /[{}[\]<>]/.test(message);

  // If no technical indicators, pass through the message (with empty check)
  if (!hasTechnicalIndicators && !looksLikeRawTechnicalDetail) {
    return (
      message.trim() ||
      'Something went wrong. Try again, and get in touch if it keeps happening.'
    );
  }

  // Fallback for unhandled technical errors
  return 'Something went wrong. Try again, and get in touch if it keeps happening.';
}
